type StripeFactory = (...args: unknown[]) => Record<string, unknown> | null;

const withSpeltAppearance = (options: unknown): unknown => {
  if (!options || typeof options !== 'object' || !('appeareance' in options)) {
    return options;
  }

  const { appeareance, ...rest } = options as Record<string, unknown>;

  return 'appearance' in rest ? rest : { ...rest, appearance: appeareance };
};

/**
 * Runs Spreedly's `createStripePaymentElement` with the Payment Element it creates pinned to the
 * `tabs` layout, the one Stripe.js v3 defaulted to (a bare form for the single APM of the purchase).
 *
 * Spreedly's bundle calls `window.Stripe(publishableKey).elements({ clientSecret }).create('payment')`
 * with no options of ours, and the Stripe.js on the page is the versioned release
 * `@stripe/stripe-js` injects at import, whose default layout is the accordion. Loading v3 next to it
 * is no way out: a second Stripe.js only warns and leaves `window.Stripe` as it was.
 *
 * Spreedly also forwards our appearance under its misspelt `appeareance` key, which Stripe ignores, so
 * the Elements options get it under `appearance` (a correctly spelt `appearance` wins if Spreedly ever
 * sends one).
 *
 * `window.Stripe` is swapped only for the synchronous call, and nothing of Stripe's is mutated: the
 * instance and its Elements are seen through proxies, and the real Elements object is what reaches
 * `confirmPayment`.
 */
export default function withTabsPaymentElement<T>(create: () => T): T {
  const original = window.Stripe as unknown as StripeFactory | undefined;

  if (typeof original !== 'function') {
    return create();
  }

  const proxies = new WeakMap<object, object>();

  const elementsProxy = (elements: Record<string, unknown>) =>
    new Proxy(elements, {
      get(target, property) {
        if (property === 'create') {
          return (type: string, options?: Record<string, unknown>) =>
            (target.create as (type: string, options?: unknown) => unknown).call(
              target,
              type,
              type === 'payment' ? { layout: 'tabs', ...options } : options,
            );
        }

        const value = Reflect.get(target, property, target);

        return typeof value === 'function' ? value.bind(target) : value;
      },
    });

  const stripeProxy = (stripe: Record<string, unknown>) =>
    new Proxy(stripe, {
      get(target, property) {
        const value = Reflect.get(target, property, target);

        if (property === 'elements') {
          return (...args: unknown[]) => {
            const elements = (value as (...args: unknown[]) => Record<string, unknown>).apply(
              target,
              args.map((arg, index) => (index === 0 ? withSpeltAppearance(arg) : arg)),
            );
            const proxy = elementsProxy(elements);

            proxies.set(proxy, elements);

            return proxy;
          };
        }

        if (typeof value !== 'function') {
          return value;
        }

        // Stripe checks the Elements it is handed are its own: the proxy goes back to the original.
        return (...args: unknown[]) =>
          (value as (...args: unknown[]) => unknown).apply(
            target,
            args.map((arg) => {
              const elements = arg && typeof arg === 'object' ? (arg as { elements?: object }).elements : undefined;

              return elements && proxies.has(elements) ? { ...(arg as object), elements: proxies.get(elements) } : arg;
            }),
          );
      },
    });

  const wrapped = Object.assign((...args: unknown[]) => {
    const stripe = original(...args);

    return stripe ? stripeProxy(stripe) : stripe;
  }, original);

  (window as unknown as { Stripe: unknown }).Stripe = wrapped;

  try {
    return create();
  } finally {
    (window as unknown as { Stripe: unknown }).Stripe = original;
  }
}
